import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format, addDays, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

// Convert hex color to RGB array for jsPDF
function hexToRgb(hex) {
  const sanitized = hex.replace('#', '');
  const bigint = parseInt(sanitized.length === 3
    ? sanitized.split('').map(c => c + c).join('')
    : sanitized, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}

function firstName(n) { return (n || '').trim().split(/\s+/)[0]; }

// Group events by member id (ownerId or 'unassigned')
function groupByMember(events, collaborators) {
  const map = {};
  collaborators.forEach(c => { map[c.id] = { collab: c, events: [] }; });
  map['__unassigned__'] = { collab: { name: 'Non assigné', role: null, id: null }, events: [] };

  events.forEach(e => {
    const oid = e.resource._ownerId;
    if (oid && map[oid]) map[oid].events.push(e);
    else map['__unassigned__'].events.push(e);
  });
  return map;
}

function formatRange(periodStart, periodEnd) {
  return `${format(periodStart, 'd MMM', { locale: fr })} → ${format(periodEnd, 'd MMM yyyy', { locale: fr })}`;
}

function eventTypeLabel(type) {
  const map = {
    commercial_physical: 'RDV physique',
    commercial_video: 'RDV visio',
    collab_punctual: 'Intervention ponctuelle',
    collab_subscription: 'Intervention abonnement',
  };
  return map[type] || 'Événement';
}

function makeRow(e) {
  return [
    format(e.start, 'EEE d MMM', { locale: fr }),
    `${format(e.start, 'HH:mm')} - ${format(e.end, 'HH:mm')}`,
    e.title || '—',
    eventTypeLabel(e.resource._type),
    e.resource.client_phone || e.resource.client?.phone || '—',
    e.resource.notes ? e.resource.notes.substring(0, 80) : '',
  ];
}

function drawHeader(doc, title, periodLabel, companyName) {
  // Title bar
  doc.setFillColor(34, 47, 62);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text(title, 14, 12);
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text(periodLabel, 14, 18);
  if (companyName) {
    doc.text(companyName, doc.internal.pageSize.getWidth() - 14, 12, { align: 'right' });
  }
  const printedOn = `Imprimé le ${format(new Date(), 'd MMM yyyy à HH:mm', { locale: fr })}`;
  doc.text(printedOn, doc.internal.pageSize.getWidth() - 14, 18, { align: 'right' });
  doc.setTextColor(0, 0, 0);
}

function drawLegend(doc, eventTypes, startY) {
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.text('Légende', 14, startY);
  let x = 32;
  let y = startY;
  Object.entries(eventTypes).forEach(([key, def]) => {
    const [r, g, b] = hexToRgb(def.bg);
    doc.setFillColor(r, g, b);
    doc.roundedRect(x, y - 3, 4, 4, 0.5, 0.5, 'F');
    doc.setFont(undefined, 'normal');
    doc.text(def.label, x + 6, y);
    x += doc.getTextWidth(def.label) + 14;
  });
  doc.setFont(undefined, 'normal');
  return startY + 8;
}

function buildEventsTable(doc, events, startY, resolveStyle, eventTypes) {
  const sorted = [...events].sort((a, b) => a.start - b.start);
  const rows = sorted.map(makeRow);
  // Pre-compute fill colors (one per row, based on event type/category)
  const fillColors = sorted.map(e => {
    if (resolveStyle) {
      const s = resolveStyle(e);
      return s ? hexToRgb(s.bg) : [120, 120, 120];
    }
    // Fallback to eventTypes by _type
    const t = e.resource?._type;
    if (t && eventTypes && eventTypes[t]?.bg) {
      return hexToRgb(eventTypes[t].bg);
    }
    return [120, 120, 120];
  });

  autoTable(doc, {
    head: [['Date', 'Horaire', 'Client', 'Type', 'Téléphone', 'Notes']],
    body: rows,
    startY,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 2.2, overflow: 'linebreak' },
    headStyles: { fillColor: [34, 47, 62], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: 24 },
      2: { cellWidth: 50 },
      3: { cellWidth: 36 },
      4: { cellWidth: 28 },
      5: { cellWidth: 'auto' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 3) {
        const [r, g, b] = fillColors[data.row.index];
        data.cell.styles.fillColor = [r, g, b];
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });
  return doc.lastAutoTable.finalY;
}

export function exportPlanningPDF({ events, collaborators, companyName, periodLabel, eventTypes, resolveStyle, mode = 'all' }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  if (mode === 'all') {
    drawHeader(doc, 'Planning Global', periodLabel, companyName);
    let y = 30;
    y = drawLegend(doc, eventTypes, y) + 2;
    if (events.length === 0) {
      doc.setFontSize(10);
      doc.text('Aucun événement sur cette période.', 14, y + 6);
    } else {
      buildEventsTable(doc, events, y, resolveStyle, eventTypes);
    }
    doc.save(`planning-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    return;
  }

  // mode === 'per-member' : one page per member
  const groups = groupByMember(events, collaborators);
  const entries = Object.values(groups).filter(g => g.events.length > 0 || g.collab.id);
  // Always print active members with their planning, even empty
  entries.sort((a, b) => {
    if (a.collab.role === 'commercial' && b.collab.role !== 'commercial') return -1;
    if (a.collab.role !== 'commercial' && b.collab.role === 'commercial') return 1;
    return (a.collab.name || '').localeCompare(b.collab.name || '');
  });

  let isFirst = true;
  entries.forEach(({ collab, events: memberEvents }) => {
    if (!isFirst) doc.addPage();
    isFirst = false;

    const memberLabel = collab.role === 'commercial'
      ? `Commercial · ${collab.name}`
      : (collab.id ? `Collaborateur · ${collab.name}` : 'Non assigné');
    drawHeader(doc, `Planning — ${firstName(collab.name) || 'Non assigné'}`, periodLabel, companyName);

    // Subtitle
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text(memberLabel, 14, 30);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.text(`${memberEvents.length} événement(s)`, 14, 36);

    let y = drawLegend(doc, eventTypes, 44) + 2;
    if (memberEvents.length === 0) {
      doc.setFontSize(10);
      doc.text('Aucun événement sur cette période.', 14, y + 6);
    } else {
      buildEventsTable(doc, memberEvents, y, resolveStyle, eventTypes);
    }
  });

  doc.save(`planning-par-membre-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
}

// Helper to compute period label from view + selectedDate
export function periodLabelFor(view, date) {
  if (view === 'day') return format(date, 'EEEE d MMMM yyyy', { locale: fr });
  if (view === 'month') return format(date, 'MMMM yyyy', { locale: fr });
  const ws = startOfWeek(date, { weekStartsOn: 1 });
  return formatRange(ws, addDays(ws, 6));
}
